import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { ethers, InterfaceAbi } from 'ethers';
import CONTRACTS from '../config/contracts';
import { LotteryDetails, TicketPurchaseParams, Ticket } from './interfaces';
import { LotteryCoreABI, TicketManagerABI } from './abis/modules';
import PrizeManagerABI from './abis/PrizeManager.json';
import WinningsManagerABI from './abis/WinningsManager.json';
import { useNetwork } from '../hooks/useNetwork';

// Optimized in-memory cache
const cache = {
  lotteryDetails: {
    data: null as LotteryDetails | null,
    timestamp: 0,
    CACHE_DURATION: 1 * 1000, // 5 seconds
  },
};

// Enum for lottery states
export enum LotteryState {
  IDLE = 0,
  ACTIVE = 1,
  DRAWING = 2,
  COMPLETED = 3,
  CANCELLED = 4,
}

// Helper function to convert Unix timestamp to human-readable date
const formatTimestamp = (timestamp: number): string => {
  try {
    return new Date(timestamp * 1000).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.warn('Error formatting timestamp:', error);
    return 'Invalid Date';
  }
};

// Helper function to format BigInt to ETH
export const formatEth = (weiAmount: bigint): string => {
  try {
    const ethAmount = Number(weiAmount) / 1_000_000_000_000_000_000;
    return `${ethAmount.toFixed(4)} ETH`;
  } catch (error) {
    console.warn('Error formatting ETH:', error);
    return 'Invalid Amount';
  }
};

export const useLotteryContract = () => {
  // Use useRef to store contract instances, preventing unnecessary re-renders
  const contractInstances = useRef<{
    lottery?: ethers.Contract;
    ticket?: ethers.Contract;
    prizeManager?: ethers.Contract;
    winningsManager?: ethers.Contract;
  }>({});
  const [signer, setSigner] = useState<ethers.Signer | null>(null);
  const { isCorrectNetwork, isConnected, connectWallet, switchNetwork } =
    useNetwork();

  // Memoized contract initialization
  const initContracts = useMemo(
    () => async () => {
      if (window.ethereum && isConnected && isCorrectNetwork) {
        try {
          const provider = new ethers.BrowserProvider(window.ethereum);
          const signer = await provider.getSigner();
          setSigner(signer);

          if (!contractInstances.current.lottery) {
            contractInstances.current.lottery = new ethers.Contract(
              CONTRACTS.LotteryCore,
              LotteryCoreABI as InterfaceAbi,
              signer
            );
          }

          if (!contractInstances.current.ticket) {
            contractInstances.current.ticket = new ethers.Contract(
              CONTRACTS.TicketManager,
              TicketManagerABI as InterfaceAbi,
              signer
            );
          }

          if (!contractInstances.current.prizeManager) {
            contractInstances.current.prizeManager = new ethers.Contract(
              CONTRACTS.PrizeManager,
              PrizeManagerABI.abi as unknown as InterfaceAbi,
              signer
            );
          }

          if (!contractInstances.current.winningsManager) {
            contractInstances.current.winningsManager = new ethers.Contract(
              CONTRACTS.WinningsManager,
              WinningsManagerABI.abi as unknown as InterfaceAbi,
              signer
            );
          }

          return contractInstances.current;
        } catch (error) {
          console.error('Error initializing contracts:', error);
          return null;
        }
      }
      return null;
    },
    [isConnected, isCorrectNetwork]
  );

  const ensureConnection = useCallback(async (forceConnect: boolean = false) => {
    if (!window.ethereum) return false;
    
    // Only attempt connection if explicitly requested
    if (!isConnected && forceConnect) {
      try {
        if (!(await connectWallet())) return false;
      } catch {
        return false;
      }
    } else if (!isConnected) {
      return false;
    }

    // Only switch network if already connected
    if (isConnected && !isCorrectNetwork) {
      try {
        if (!(await switchNetwork())) return false;
      } catch {
        return false;
      }
    }
    
    return isConnected && isCorrectNetwork;
  }, [isConnected, isCorrectNetwork, connectWallet, switchNetwork]);

  const getCurrentLotteryId = useCallback(async (): Promise<number | null> => {
    try {
      const lotteryContract = contractInstances.current.lottery;
      if (!lotteryContract) return null;

      const idMethods = ['currentLotteryId'];

      for (const method of idMethods) {
        try {
          if (
            lotteryContract[method] &&
            typeof lotteryContract[method] === 'function'
          ) {
            const rawId = await lotteryContract[method]();
            return typeof rawId === 'bigint'
              ? Number(rawId)
              : typeof rawId === 'number'
              ? rawId
              : Number(rawId.toString());
          }
        } catch {
          continue;
        }
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const getLotteryDetails =
    useCallback(async (): Promise<LotteryDetails | null> => {
      if (
        cache.lotteryDetails.data &&
        Date.now() - cache.lotteryDetails.timestamp <
          cache.lotteryDetails.CACHE_DURATION
      ) {
        return cache.lotteryDetails.data;
      }

      try {
        // Don't force connection for initial lottery details fetch
        if (!(await ensureConnection(false))) return null;

        const lotteryContract = contractInstances.current.lottery;
        const prizeManagerContract = contractInstances.current.prizeManager;

        if (!lotteryContract || !prizeManagerContract) {
          await initContracts();
          if (
            !contractInstances.current.lottery ||
            !contractInstances.current.prizeManager
          )
            return null;
        }

        const currentLotteryId = await getCurrentLotteryId();
        if (currentLotteryId === null) return null;

        let lotteryData;
        try {
          lotteryData =
            await contractInstances.current.lottery!.getLotteryDetails();
        } catch (error) {
          console.error('Error fetching lottery details from contract:', error);
          return null;
        }

        const extractValue = (
          proxyResult: any,
          index: number,
          defaultValue: any = 0
        ) => {
          try {
            const value = proxyResult[index];
            return value !== undefined ? value : defaultValue;
          } catch {
            return defaultValue;
          }
        };

        const details: LotteryDetails = {
          startTime: Number(
            extractValue(lotteryData, 0, Math.floor(Date.now() / 1000))
          ),
          endTime: Number(
            extractValue(lotteryData, 1, Math.floor(Date.now() / 1000) + 86400)
          ),
          price: extractValue(lotteryData, 2, BigInt(0)),
          numbers: Number(extractValue(lotteryData, 3, 5)),
          maxNum: Number(extractValue(lotteryData, 4, 50)),
          state: Number(extractValue(lotteryData, 5, 0)),
          pool: extractValue(lotteryData, 6, BigInt(0)),
          totalPool: extractValue(lotteryData, 7, BigInt(0)),
        };

        let winningNumbers: number[] | undefined;
        try {
          let previousLotteryId =
            currentLotteryId > 0 ? currentLotteryId - 1 : 0;
          const rawWinningNumbers =
            await contractInstances.current.prizeManager!.getWinningNumbers(
              previousLotteryId
            );
          if (rawWinningNumbers && rawWinningNumbers.length > 0) {
            winningNumbers = Array.from(rawWinningNumbers)
              .map(n => Number(n))
              .sort((a, b) => a - b);
          }
        } catch (error) {
          console.error('Error fetching winning numbers:', error);
        }

        const enrichedDetails = {
          ...details,
          formattedStartTime: formatTimestamp(details.startTime),
          formattedEndTime: formatTimestamp(details.endTime),
          formattedTicketPrice: formatEth(details.price),
          formattedPool: formatEth(details.pool),
          formattedTotalPool: formatEth(details.totalPool),
          stateName: LotteryState[details.state] || 'UNKNOWN',
          timeRemaining: details.endTime - Math.floor(Date.now() / 1000),
          lotteryId: currentLotteryId,
          winningNumbers: winningNumbers?.map(Number),
        };

        cache.lotteryDetails.data = enrichedDetails;
        cache.lotteryDetails.timestamp = Date.now();
        return enrichedDetails;
      } catch (error) {
        console.error('Error in getLotteryDetails:', error);
        return null;
      }
    }, [ensureConnection, getCurrentLotteryId, initContracts]);

  const getUserTickets = useCallback(async (): Promise<Ticket[] | null> => {
    try {
      if (!(await ensureConnection())) return null;

      const ticketContract = contractInstances.current.ticket;
      const lotteryContract = contractInstances.current.lottery;

      if (!ticketContract || !lotteryContract) {
        await initContracts();
        if (
          !contractInstances.current.ticket ||
          !contractInstances.current.lottery
        )
          return null;
      }

      const currentLotteryId = await getCurrentLotteryId();
      if (!currentLotteryId) return null;

      const currentSigner = await getSigner();
      if (!currentSigner) return null;
      const signerAddress = await currentSigner.getAddress();

      const rawTickets =
        await contractInstances.current.ticket!.getPlayerTickets(
          currentLotteryId,
          signerAddress
        );

      return rawTickets.map((ticket: any, index: number) => ({
        id: `${currentLotteryId}-${index}`,
        lotteryId: currentLotteryId,
        numbers: Array.from(ticket.numbers).map(Number),
        purchaseTime: Number(ticket.purchaseTime),
        isWinner: false,
      }));
    } catch (error) {
      console.error('Error fetching user tickets:', error);
      return null;
    }
  }, [ensureConnection, getCurrentLotteryId, initContracts]);

  const getTotalTickets = useCallback(
    async (lotteryId?: number): Promise<number | null> => {
      try {
        if (!(await ensureConnection())) return null;

        const ticketContract = contractInstances.current.ticket;
        if (!ticketContract) {
          await initContracts();
          if (!contractInstances.current.ticket) return null;
        }

        const resolvedLotteryId = lotteryId ?? (await getCurrentLotteryId());
        if (resolvedLotteryId === null || resolvedLotteryId === undefined) return null;

        const rawTotal = await contractInstances.current.ticket!.getTotalTickets(
          resolvedLotteryId
        );

        const total = Number(rawTotal);
        if (process.env.NODE_ENV === 'development') {
          console.debug('[Lottery] getTotalTickets', {
            lotteryId: resolvedLotteryId,
            total,
          });
        }
        return Number.isFinite(total) ? total : null;
      } catch (error) {
        console.error('Error fetching total tickets:', error);
        return null;
      }
    },
    [ensureConnection, getCurrentLotteryId, initContracts]
  );

  const getSigner = useCallback(async () => {
    if (!signer) {
      if (!window.ethereum) {
        throw new Error('No Ethereum provider found. Please install MetaMask.');
      }
      const provider = new ethers.BrowserProvider(window.ethereum as any);
      const signerInstance = await provider.getSigner();
      setSigner(signerInstance);
      return signerInstance;
    }
    return signer;
  }, [signer]);

  const purchaseTicket = useCallback(
    async ({ numbers, value }: TicketPurchaseParams): Promise<boolean> => {
      const ticketContract = contractInstances.current.ticket;
      if (!ticketContract || !(await ensureConnection(true))) {
        console.error('Contracts not initialized or not connected');
        throw new Error('Contracts not initialized or not connected');
      }

      try {
        if (!Array.isArray(numbers) || numbers.length === 0) {
          throw new Error('Invalid numbers selected');
        }

        const currentSigner = await getSigner();
        if (!currentSigner) {
          throw new Error('No signer available. Please connect your wallet.');
        }
        const userAddress = await currentSigner.getAddress();

        const txParams = { value };
        const tx = await ticketContract.purchaseTicket(
          userAddress,
          numbers,
          txParams
        );
        console.log('Transaction sent:', tx.hash);
        await tx.wait(1);
        console.log('Transaction confirmed');

        cache.lotteryDetails.timestamp = 0; // Invalidate cache

        return true;
      } catch (error: any) {
        console.error('Error purchasing ticket:', error);
        throw new Error(error.message || 'Failed to purchase ticket');
      }
    },
    [ensureConnection, getSigner]
  );

  const checkUserWinnings = useCallback(
    async (lotteryId?: number) => {
      try {
        if (!(await ensureConnection(true))) {
          console.warn('Connection not established');
          return null;
        }

        // Ensure contracts are initialized
        await initContracts();

        const ticketContract = contractInstances.current.ticket;
        const prizeManagerContract = contractInstances.current.prizeManager;

        if (!ticketContract || !prizeManagerContract) {
          console.warn('Ticket or Prize Manager contract not initialized');
          return null;
        }

        const currentLotteryId = lotteryId || (await getCurrentLotteryId());
        if (!currentLotteryId) return null;

        const currentSigner = await getSigner();
        if (!currentSigner) return null;
        const signerAddress = await currentSigner.getAddress();

        const winningNumbers = await prizeManagerContract.getWinningNumbers(
          currentLotteryId
        );
        if (!winningNumbers || winningNumbers.length === 0) return null;
        const sortedWinningNumbers = Array.from(winningNumbers)
          .map(Number)
          .sort((a, b) => a - b);

        const userTickets = await ticketContract.getPlayerTickets(
          currentLotteryId,
          signerAddress
        );

        let tier1Winnings = false;
        let tier2Winnings = false;
        let tier3Winnings = false;
        let totalWinnings = BigInt(0);

        for (const ticket of userTickets) {
          const ticketNumbers = Array.from(ticket.numbers)
            .map(Number)
            .sort((a, b) => a - b);
          const matchingNumbers = ticketNumbers.filter(num =>
            sortedWinningNumbers.includes(num)
          );

          if (
            JSON.stringify(ticketNumbers) ===
            JSON.stringify(sortedWinningNumbers)
          ) {
            tier1Winnings = true;
            totalWinnings += await prizeManagerContract.calculateTierPrize(
              currentLotteryId,
              1
            );
          } else if (matchingNumbers.length === 4) {
            tier2Winnings = true;
            totalWinnings += await prizeManagerContract.calculateTierPrize(
              currentLotteryId,
              2
            );
          } else if (matchingNumbers.length === 3) {
            tier3Winnings = true;
            totalWinnings += await prizeManagerContract.calculateTierPrize(
              currentLotteryId,
              3
            );
          }
        }

        return { tier1Winnings, tier2Winnings, tier3Winnings, totalWinnings };
      } catch (error) {
        console.error('Error checking user winnings:', error);
        return null;
      }
    },
    [ensureConnection, getCurrentLotteryId, getSigner, initContracts]
  );

  const withdrawWinnings = useCallback(async (): Promise<{
    success: boolean;
    message: string;
    pendingWithdrawal?: bigint;
  }> => {
    try {
      if (!(await ensureConnection())) {
        return { success: false, message: 'Failed to establish connection' };
      }

      // Ensure contracts are initialized
      await initContracts();

      const winningsManagerContract = contractInstances.current.winningsManager;
      if (!winningsManagerContract) {
        console.warn('Winnings manager contract not initialized');
        return {
          success: false,
          message: 'Winnings manager contract not initialized',
        };
      }

      const currentSigner = await getSigner();
      if (!currentSigner) {
        return { success: false, message: 'Signer not available' };
      }
      const signerAddress = await currentSigner.getAddress();

      const pendingWithdrawal =
        await winningsManagerContract.pendingWithdrawals(signerAddress);
      if (pendingWithdrawal === BigInt(0)) {
        return {
          success: false,
          message: 'No winnings available for withdrawal',
        };
      }

      const tx = await winningsManagerContract.withdrawWinnings();
      await tx.wait();

      const pendingAfterWithdrawal =
        await winningsManagerContract.pendingWithdrawals(signerAddress);

      return {
        success: true,
        message: `Successfully withdrew ${formatEth(pendingWithdrawal)} ETH`,
        pendingWithdrawal: pendingAfterWithdrawal,
      };
    } catch (error: any) {
      console.error('Error withdrawing winnings:', error);
      return {
        success: false,
        message: error.message || 'Failed to withdraw winnings',
      };
    }
  }, [ensureConnection, getSigner, initContracts, formatEth]);

  const setupContractListeners = useCallback(async () => {
    const lotteryContract = contractInstances.current.lottery;
    const ticketContract = contractInstances.current.ticket;

    if (!lotteryContract || !ticketContract) return () => {};

    const handleLotteryStateChanged = async () => {
      console.log('Lottery state changed, updating details');
      cache.lotteryDetails.timestamp = 0; // Invalidate cache
      await getLotteryDetails();
    };

    const handleTicketPurchased = async (lotteryId: bigint, player: string) => {
      console.log(`Ticket purchased in lottery ${lotteryId} by ${player}`);
      cache.lotteryDetails.timestamp = 0; // Invalidate cache
      await getLotteryDetails();
    };

    try {
      lotteryContract.on('LotteryStateChanged', handleLotteryStateChanged);
      ticketContract.on('TicketPurchased', handleTicketPurchased);

      return () => {
        lotteryContract.off('LotteryStateChanged', handleLotteryStateChanged);
        ticketContract.off('TicketPurchased', handleTicketPurchased);
      };
    } catch (error) {
      console.error('Error setting up contract listeners:', error);
      return () => {};
    }
  }, [getLotteryDetails]);

  useEffect(() => {
    setupContractListeners();
  }, [setupContractListeners]);

  return {
    lotteryContract: contractInstances.current.lottery,
    ticketContract: contractInstances.current.ticket,
    prizeManagerContract: contractInstances.current.prizeManager,
    winningsManagerContract: contractInstances.current.winningsManager,
    signer,
    isCorrectNetwork,
    isConnected,
    connectWallet,
    switchNetwork,
    getLotteryDetails,
    getUserTickets,
    getTotalTickets,
    purchaseTicket,
    checkUserWinnings,
    withdrawWinnings,
  };
};
